import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Catalog } from 'app/Model/Catalog/Catalog';
import { Message } from 'app/Model/System/Message';
import { PimLicense } from 'app/Model/User/PimLicense';
import { CatalogService } from 'app/Services/CatalogManagement/catalog.service';
import { LoginService } from 'app/Services/login.service';
import { SystemService } from 'app/Services/system.service';
import { UserManagementService } from 'app/Services/userManagment.service';
import * as ButtonGroup from 'devextreme/ui/button_group';
import { custom } from 'devextreme/ui/dialog';

@Component({
  selector: 'np-catalog-card',
  templateUrl: './catalog-card.component.html',
  styleUrl: './catalog-card.component.css'
})
export class CatalogCardComponent implements OnInit, OnDestroy {
  @Input() model: Catalog;
  @Input() showDuplicateButton = true;
  @Input() showCopyButton = true;
  @Input() showRestoreButton = true;
  @Input() showEditButton = true;
  @Input() showDeleteButton = true;

  buttons: ButtonGroup.Item[];
  loading = false;
  cacheBuster = 0;

  constructor(
    public loginService: LoginService,
    public translate: TranslateService,
    public systemService: SystemService,
    public catalogService: CatalogService,
    private userManagment: UserManagementService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.buttons = [];
    if (this.showDuplicateButton) {
      this.buttons.push({ icon: 'copy', hint: this.translate.instant('Katalog duplizieren') });
    }
    if (this.showCopyButton) {
      this.buttons.push({ icon: 'description', hint: this.translate.instant('Katalog kopieren') });
    }
    if (this.showRestoreButton) {
      this.buttons.push({ icon: 'clock', hint: this.translate.instant('Wiederherstellungspunkt erstellen') });
    }
    if (this.showEditButton) {
      this.buttons.push({ icon: 'edit', hint: this.translate.instant('Bearbeiten') });
    }
    if (this.showDeleteButton) {
      this.buttons.push({ icon: 'trash', hint: this.translate.instant('Löschen') });
    }
    this.cacheBuster = Date.now();
  }

  ngOnDestroy(): void {}

  onButtonClick(event: ButtonGroup.ItemClickEvent) {
    switch (event.itemData.icon) {
      case 'copy':
        this.duplicateCatalog();
        break;
      case 'description':
        this.copyCatalog();
        break;
      case 'clock':
        this.createRestorePoint();
        break;
      case 'edit':
        this.editCatalog();
        break;
      case 'trash':
        this.deleteCatalog();
        break;
    }
  }

  update() {
    this.catalogService.catalog = this.model;
    this.catalogService.updateCatalog();
  }

  canView(name: string): boolean {
    return this.userManagment.canView(name);
  }

  canEdit(name: string): boolean {
    return this.userManagment.canEdit(name);
  }

  private copyCatalog() {
    console.log('Katalog kopiert:', this.model);
  }

  private duplicateCatalog() {
    const text = this.translate.instant('Der Katalog wird dupliziert');
    this.catalogService.duplicateCatalog(this.model.id);
    this.systemService.notify(new Message(text));
  }

  private createRestorePoint() {
    const text = this.translate.instant('Ein Wiederherstellungspunkt wird per Job erzeugt');
    this.catalogService.createRestorePoint(this.model.id);
    this.systemService.notify(new Message(text));
  }

  private editCatalog() {
    this.loading = true;
    console.log(this.model);
    this.router.navigate(['/catalog', this.loginService.currentCustomer.id, this.model.id, 'info']).then(() => (this.loading = false));
  }

  private deleteCatalog() {
    const catalogToDeleteIsVirtual = this.model.isVirtual;
    if (!catalogToDeleteIsVirtual && this.catalogService.catalogs.some((catalog) => catalog.isVirtual)) {
      this.systemService.notify(
        new Message(this.translate.instant('Prüfe Verknüpfungen zu virtuellen Katalogen.')),
        1000
      );
      this.loading = true;
      this.catalogService
        .checkVirtualCatalogs(this.model.id, this.loginService.currentCustomer.id)
        .then((result: Record<string, number>) => {
          this.loading = false;
          if (Object.keys(result).length > 0) {
            this.showDeleteDialogWithVirtualCatalogInfo(result);
          } else {
            this.showDeleteDialog();
          }
        });
    } else {
      this.showDeleteDialog();
    }
  }

  private showDeleteDialogWithVirtualCatalogInfo(result: Record<string, number>) {
    var rootUrl = window.location.origin;
    var catalog: Catalog;
    let messageText =
      '<p>' +
      this.translate.instant('DeleteNoteVirtualCatalogs') +
      " <a href='https://knowledge.nextpim.de/wichtige-funktionen#virtuelle-kataloge' target='_blank'>nextPIM Wiki</a>.</p><p>Anzahl Verlinkungen:</p>" +
      '<table><th>Katalog</th><th>Anzahl</th>';
    for (const [key, value] of Object.entries(result)) {
      catalog = this.catalogService.catalogs.find((cat) => cat.id == key);
      if (catalog) {
        messageText += `<tr><td><a href="${rootUrl}/catalog/${this.loginService.currentCustomer.id}/${catalog.id}/info">${catalog.catalogName}</a></td><td style="text-align: center; vertical-align: middle;">${value}</td></tr>`;
      }
    }
    messageText += '</table>';

    let virtualCatalogDialog = custom({
      showTitle: true,
      title: this.translate.instant('Katalog löschen'),
      dragEnabled: false,
      messageHtml: messageText,

      buttons: [
        {
          text: 'Löschen bestätigen',
          onClick: (e) => {
            this.onDeleteClicked();
          }
        },
        {
          text: 'Abbrechen',
          onClick: (e) => {}
        }
      ]
    });
    virtualCatalogDialog.show();
  }

  private showDeleteDialog() {
    let normalCatalogDialog = custom({
      showTitle: true,
      title: this.translate.instant('Katalog löschen'),
      dragEnabled: false,
      messageHtml: this.translate.instant('Wollen Sie den Katalog wirklich löschen?'),

      buttons: [
        {
          text: this.translate.instant('Ja'),
          onClick: (e) => {
            this.onDeleteClicked();
          }
        },
        {
          text: this.translate.instant('Nein')
        }
      ]
    });
    normalCatalogDialog.show();
  }

  private onDeleteClicked() {
    if (
      this.loginService.currentCustomer.pimLicense == PimLicense.Onboarding &&
      this.catalogService.catalogs.length > 0 &&
      this.model.isDemoCatalog &&
      this.catalogService.catalogs.filter((cat) => cat.isDemoCatalog).length == 1
    ) {
      this.systemService.notifyInfo(
        this.translate.instant(
          'Demo-Katalog kann nicht gelöscht werden. Es muss mindestens ein Demo-Katalog vorhanden sein.'
        )
      );
    } else {
      this.catalogService.remCatalog(this.model.id);
    }
    this.refresh();
  }

  private refresh() {
    this.catalogService.selectedCatalogId = null;
    this.catalogService.isJobRunning = false;
    this.catalogService.importCatalogVisible = false;
    if (this.loginService.currentUser == null) {
      this.router.navigate(['/']);
      return;
    }
    this.catalogService.catalog = null;

    // this.loadCatalogs();

    if (this.loginService.currentUser.newUser) {
      this.loginService.currentUser.newUser = false;
      this.loginService.updateUser(this.loginService.currentUser);
    }
  }
}
